import { useWebsite, useServices } from '../context/WebsiteContext';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const { cart } = useCart();
  const { isAuthenticated, isAdmin } = useAuth();
  const { customer } = useCustomerAuth();
  const isCustomerLoggedIn = !!customer;
  const portalDestination = isAdmin ? '/admin' : (isAuthenticated || isCustomerLoggedIn) ? '/portal' : '/portal/login';
  const portalLabel = isAdmin ? 'Admin' : (isAuthenticated || isCustomerLoggedIn) ? 'My Portal' : 'Client Portal';
  const isLoggedIn = isAuthenticated || isCustomerLoggedIn;
  const location = useLocation();
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { fetchAnnouncement(); }, []);

  // Close dropdown on route change
  useEffect(() => {
    setServicesOpen(false);
    setMobileServicesOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/announcement`);
      if (res.data) setAnnouncement(res.data);
    } catch (_) {}
  };

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setServicesOpen(false), 200);
  };

  const { config } = useWebsite();
  const navLinks = config.navigation;
  const services = useServices();
  const compactNav = navLinks.length > 7 || navLinks.some(n=>n.name.length>18);


  const isActive = (path) => location.pathname === path;
  const isServicesActive = location.pathname.startsWith('/services');

  return (
    <>
      {announcement && (
        <div
          className="py-2 px-4 text-center text-sm font-medium"
          style={{
            backgroundColor: announcement.background_color || '#F5A623',
            color: announcement.text_color || '#000000',
          }}
          data-testid="announcement-banner"
        >
          <span>{announcement.text}</span>
          {announcement.link_url && (
            <a href={announcement.link_url} className="ml-2 underline font-semibold hover:no-underline">
              {announcement.link_text || 'Learn More'}
            </a>
          )}
        </div>
      )}

      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-lg shadow-lg py-0'
            : 'bg-white py-0'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center group" data-testid="logo-link">
              <img
                src={config.logo}
                alt="Kre8ing Your IdeaS"
                className={`w-auto transition-all duration-300 ${isScrolled ? 'h-10' : 'h-14'}`}
              />
            </Link>

            {/* Desktop nav */}
            <nav className={`${compactNav ? "hidden" : "hidden xl:flex"} items-center space-x-1`}>
              {navLinks.map((link) =>
                link.hasDropdown ? (
                  <div
                    key={link.path}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Link
                      to={link.path}
                      className={`nav-link-animated px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                        isServicesActive
                          ? 'text-[#4A90A4] active'
                          : 'text-gray-600 hover:text-[#4A90A4]'
                      }`}
                      data-testid="nav-services"
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                    </Link>

                    {/* Dropdown */}
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
                        servicesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                      }`}
                      data-testid="services-dropdown"
                    >
                      <div className="w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="p-2">
                          {services.map((svc) => (
                            <Link
                              key={svc.slug}
                              to={`/services/${svc.slug}`}
                              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all group ${
                                location.pathname === `/services/${svc.slug}`
                                  ? 'bg-[#4A90A4]/5 text-[#4A90A4]'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                              data-testid={`dropdown-${svc.slug}`}
                            >
                              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                <img src={svc.image} alt="" className="w-full h-full object-cover" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold truncate group-hover:text-[#4A90A4] transition-colors">{svc.name}</p>
                                <p className="text-xs text-gray-400 truncate">{svc.tagline}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                        <div className="border-t border-gray-100 p-2">
                          <Link
                            to="/services"
                            className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-[#4A90A4] hover:bg-[#4A90A4]/5 rounded-xl transition-colors"
                            data-testid="dropdown-view-all"
                          >
                            View All Services
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link-animated px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-[#4A90A4] active'
                        : 'text-gray-600 hover:text-[#4A90A4]'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </nav>

            <div className="flex items-center space-x-3">
              <Link
                to="/cart"
                className="relative p-2.5 text-gray-600 hover:text-[#4A90A4] transition-colors rounded-full hover:bg-gray-100"
                data-testid="cart-link"
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                {cart.item_count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#F5A623] text-gray-900 text-xs font-bold rounded-full flex items-center justify-center shadow-md">
                    {cart.item_count}
                  </span>
                )}
              </Link>

              {isLoggedIn ? (
                <Link
                  to={portalDestination}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 border border-[#4A90A4] text-[#4A90A4] text-xs font-medium rounded-full hover:bg-[#4A90A4] hover:text-white transition-all"
                  data-testid="account-link"
                >
                  <User className="w-3.5 h-3.5" /> {portalLabel}
                </Link>
              ) : (
                <Link
                  to="/portal/login"
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 border border-[#4A90A4] text-[#4A90A4] text-xs font-medium rounded-full hover:bg-[#4A90A4] hover:text-white transition-all"
                  data-testid="portal-login-link"
                >
                  <User className="w-3.5 h-3.5" /> Client Portal
                </Link>
              )}

              <Link
                to={config.primary_link}
                className="hidden sm:inline-flex items-center px-6 py-2.5 bg-[#F5A623] text-gray-900 font-bold text-sm rounded-full btn-glow shadow-md"
                data-testid="shop-now-btn"
              >
                {config.primary_label}
              </Link>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`${compactNav ? "" : "xl:hidden"} p-2.5 text-gray-600 hover:text-[#4A90A4] transition-colors rounded-full hover:bg-gray-100`}
                data-testid="mobile-menu-btn"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        <div
          className={`${compactNav ? "" : "xl:hidden"} overflow-hidden transition-all duration-500 ease-in-out ${
            isMenuOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-white border-t border-gray-100 shadow-xl px-4 py-6 space-y-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path}>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-base font-medium transition-all ${
                      isServicesActive
                        ? 'text-[#4A90A4] bg-[#4A90A4]/5'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid="mobile-services-toggle"
                  >
                    <span>{link.name}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${mobileServicesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="pl-4 py-1 space-y-0.5">
                      {services.map((svc) => (
                        <Link
                          key={svc.slug}
                          to={`/services/${svc.slug}`}
                          onClick={() => setIsMenuOpen(false)}
                          className={`flex items-center gap-3 py-2.5 px-4 rounded-xl text-sm transition-all ${
                            location.pathname === `/services/${svc.slug}`
                              ? 'text-[#4A90A4] bg-[#4A90A4]/5'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                          data-testid={`mobile-service-${svc.slug}`}
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                            <img src={svc.image} alt="" className="w-full h-full object-cover" />
                          </div>
                          <span className="font-medium">{svc.name}</span>
                        </Link>
                      ))}
                      <Link
                        to="/services"
                        onClick={() => setIsMenuOpen(false)}
                        className="block py-2.5 px-4 text-sm font-semibold text-[#4A90A4] hover:bg-[#4A90A4]/5 rounded-xl"
                      >
                        View All Services
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 px-4 rounded-xl text-base font-medium transition-all ${
                    isActive(link.path)
                      ? 'text-[#4A90A4] bg-[#4A90A4]/5'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <Link
              to={portalDestination}
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 border-2 border-[#4A90A4] text-[#4A90A4] font-bold rounded-full mt-4 hover:bg-[#4A90A4] hover:text-white transition-all"
              data-testid="mobile-portal-link"
            >
              <User className="w-4 h-4" /> {portalLabel}
            </Link>
            <Link
              to={config.primary_link}
              onClick={() => setIsMenuOpen(false)}
              className="block w-full text-center py-3 bg-[#F5A623] text-gray-900 font-bold rounded-full mt-3 btn-glow"
            >
              Shop Now
            </Link>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
